// src/data/weekUnlockStorage.js
// Freigeschaltete Wochen + zuletzt bekannte EXP pro Woche.
// Freischaltung: lokal (localStorage) und in Supabase (profile_settings.unlocked_week).
// Es zählt immer der höhere Wert – eine freigeschaltete Woche wird nie wieder gesperrt.
// Dazu der Zeitpunkt der letzten Freischaltung (profile_settings.unlocked_at), damit die
// nächste Woche bei 90 % erst ab dem folgenden Montag aufgeht (weekExp.js).

import { supabase } from "../supabaseClient";
import {
  loadUnlockedWeekRemote,
  saveUnlockedWeekRemote,
  loadUnlockedAtRemote,
  saveUnlockedAtRemote,
} from "../lib/remoteProfiles";

const UNLOCK_KEY_PREFIX = "fluent_unlockedweek_";
const EXP_CACHE_KEY_PREFIX = "fluent_weekexp_";
const UNLOCKED_AT_KEY_PREFIX = "fluent_unlockedat_";

function configKey(languageId, pace, profileId) {
  return `${profileId || "default"}_${languageId || "generic"}_${
    pace || "medium"
  }`;
}

// getSession liest die gespeicherte Sitzung lokal (kein Server-Aufruf wie bei getUser)
async function getUserId() {
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.user?.id || null;
  } catch (e) {
    return null;
  }
}

/** Sofort verfügbarer lokaler Stand, ohne auf Supabase zu warten */
export function loadUnlockedWeekLocal(languageId, pace, profileId) {
  if (typeof window === "undefined") return 1;
  try {
    const raw = window.localStorage.getItem(
      UNLOCK_KEY_PREFIX + configKey(languageId, pace, profileId)
    );
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  } catch (e) {
    return 1;
  }
}

function saveUnlockedWeekLocal(languageId, pace, profileId, week) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      UNLOCK_KEY_PREFIX + configKey(languageId, pace, profileId),
      String(week)
    );
  } catch (e) {
    console.warn("Fluent! unlocked week save error (local):", e);
  }
}

export async function loadUnlockedWeek(languageId, pace, profileId) {
  const local = loadUnlockedWeekLocal(languageId, pace, profileId);
  const userId = await getUserId();
  if (!userId) return local;

  try {
    const remote = await loadUnlockedWeekRemote(
      userId,
      profileId || "default",
      languageId,
      pace
    );
    const week = Math.max(local, remote || 1);
    // Lokal nachziehen, damit der nächste Seitenaufruf gleich den richtigen Stand zeigt
    if (week > local) saveUnlockedWeekLocal(languageId, pace, profileId, week);
    return week;
  } catch (e) {
    // z. B. Spalte unlocked_week existiert (noch) nicht
    console.warn("Fluent! unlocked week load error (remote):", e);
    return local;
  }
}

export async function saveUnlockedWeek(languageId, pace, profileId, week) {
  saveUnlockedWeekLocal(languageId, pace, profileId, week);
  const userId = await getUserId();
  if (!userId) return;

  try {
    await saveUnlockedWeekRemote(
      userId,
      profileId || "default",
      languageId,
      pace,
      week
    );
  } catch (e) {
    console.warn("Fluent! unlocked week save error (remote):", e);
  }
}

function loadUnlockedAtLocal(languageId, pace, profileId) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(
      UNLOCKED_AT_KEY_PREFIX + configKey(languageId, pace, profileId)
    );
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && parsed.at ? parsed : null;
  } catch (e) {
    return null;
  }
}

function saveUnlockedAtLocal(languageId, pace, profileId, week, at) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      UNLOCKED_AT_KEY_PREFIX + configKey(languageId, pace, profileId),
      JSON.stringify({ week, at })
    );
  } catch (e) {
    console.warn("Fluent! unlocked at save error (local):", e);
  }
}

/** Merkt sich, wann `week` freigeschaltet wurde (ISO-Zeitstempel) */
export async function saveUnlockedAt(languageId, pace, profileId, week, at) {
  saveUnlockedAtLocal(languageId, pace, profileId, week, at);
  const userId = await getUserId();
  if (!userId) return;

  try {
    await saveUnlockedAtRemote(userId, profileId || "default", languageId, pace, at);
  } catch (e) {
    // z. B. Spalte unlocked_at existiert (noch) nicht
    console.warn("Fluent! unlocked at save error (remote):", e);
  }
}

/**
 * Zeitpunkt, an dem `week` freigeschaltet wurde. Gilt nur, wenn er zu genau dieser
 * Woche gehört. Ist keiner bekannt (Altbestand), zählt ab jetzt.
 */
export async function loadUnlockedAt(languageId, pace, profileId, week) {
  const local = loadUnlockedAtLocal(languageId, pace, profileId);
  if (local && local.week === week) return local.at;

  const userId = await getUserId();
  if (userId) {
    try {
      const remote = await loadUnlockedAtRemote(
        userId,
        profileId || "default",
        languageId,
        pace
      );
      if (remote && remote.week === week) {
        saveUnlockedAtLocal(languageId, pace, profileId, week, remote.at);
        return remote.at;
      }
    } catch (e) {
      console.warn("Fluent! unlocked at load error (remote):", e);
    }
  }

  const now = new Date().toISOString();
  await saveUnlockedAt(languageId, pace, profileId, week, now);
  return now;
}

export function resetUnlockedWeek(languageId, pace, profileId) {
  if (typeof window === "undefined") return;
  try {
    const key = configKey(languageId, pace, profileId);
    window.localStorage.removeItem(UNLOCK_KEY_PREFIX + key);
    window.localStorage.removeItem(EXP_CACHE_KEY_PREFIX + key);
    window.localStorage.removeItem(UNLOCKED_AT_KEY_PREFIX + key);
  } catch (e) {
    console.warn("Fluent! unlocked week reset error (local):", e);
  }
}

/** Zuletzt berechnete EXP je Woche, für die Karten, die gerade nicht im Blick sind */
export function loadWeekExpCache(languageId, pace, profileId) {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(
      EXP_CACHE_KEY_PREFIX + configKey(languageId, pace, profileId)
    );
    return raw ? JSON.parse(raw) || {} : {};
  } catch (e) {
    return {};
  }
}

export function saveWeekExpCache(languageId, pace, profileId, cache) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      EXP_CACHE_KEY_PREFIX + configKey(languageId, pace, profileId),
      JSON.stringify(cache)
    );
  } catch (e) {
    console.warn("Fluent! week exp cache save error:", e);
  }
}
